import { useState, useEffect, useCallback } from "react";
import {
  adminGetAllDocuments,
  adminDeleteDocument,
  logAdminActivity,
  type GetAllDocumentsFilters,
  type PaginationParams,
  type DocumentWithUser,
} from "@/shared/db";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";

export function useDocuments(
  initialFilters: GetAllDocumentsFilters = {},
  initialPagination: PaginationParams = { page: 1, pageSize: 50 }
) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [documents, setDocuments] = useState<DocumentWithUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] =
    useState<GetAllDocumentsFilters>(initialFilters);
  const [pagination, setPagination] =
    useState<PaginationParams>(initialPagination);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { documents: docsData, total: totalCount } =
        await adminGetAllDocuments(filters, pagination);

      setDocuments(docsData);
      setTotal(totalCount);
    } catch (err: unknown) {
      console.error("Error fetching documents:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("admin.errors.fetchDocumentsFailed");
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, t]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const updateFilters = (newFilters: Partial<GetAllDocumentsFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination({ ...pagination, page: 1 }); // Reset to first page
  };

  const updatePagination = (newPagination: Partial<PaginationParams>) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await adminDeleteDocument(documentId);
      if (user?.id) {
        await logAdminActivity(
          user.id,
          "delete_document",
          "document",
          documentId,
          { reason: "Admin moderation" }
        );
      }
      toast.success(t("admin.documents.actions.deleted"));
      fetchDocuments(); // Refresh list
    } catch (err: unknown) {
      console.error("Error deleting document:", err);
      const errorMessage =
        err instanceof Error ? err.message : t("admin.errors.deleteFailed");
      toast.error(errorMessage);
    }
  };

  return {
    documents,
    total,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    updatePagination,
    deleteDocument: handleDeleteDocument,
    refetch: fetchDocuments,
  };
}
